import { useState, useEffect, useCallback } from 'react';
import { Category, CategoryWithChildren, CreateCategoryRequest, UpdateCategoryRequest } from '../types';
import { categoryService } from '../services/categoryService';

export function useCategories() {
  const [categories, setCategories] = useState<CategoryWithChildren[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await categoryService.getAllCategories();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, []);

  const createCategory = useCallback(async (categoryData: CreateCategoryRequest) => {
    setError(null);
    try {
      await categoryService.createCategory(categoryData);
      await fetchCategories(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
      throw err;
    }
  }, [fetchCategories]);

  const updateCategory = useCallback(async (id: number, updateData: UpdateCategoryRequest) => {
    setError(null);
    try {
      await categoryService.updateCategory(id, updateData);
      await fetchCategories(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
      throw err;
    }
  }, [fetchCategories]);

  const deleteCategory = useCallback(async (id: number) => {
    setError(null);
    try {
      await categoryService.deleteCategory(id);
      await fetchCategories(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
      throw err;
    }
  }, [fetchCategories]);

  const getCategoryUsage = useCallback(async (id: number) => {
    try {
      return await categoryService.getCategoryUsage(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get category usage');
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryUsage,
    refreshCategories: fetchCategories
  };
}

export function useCategoriesByType(type: string) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategoriesByType = useCallback(async () => {
    if (!type) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await categoryService.getCategoriesByType(type);
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to fetch ${type} categories`);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    fetchCategoriesByType();
  }, [fetchCategoriesByType]);

  return {
    categories,
    loading,
    error,
    refreshCategories: fetchCategoriesByType
  };
}


